import { useState, useEffect, useCallback } from 'react';
import type { KycStatus, RiskLevel, User } from '../types';
import {
  getAuthState,
  subscribeAuth,
  login as storeLogin,
  registerFarmer as storeRegisterFarmer,
  registerHtx as storeRegisterHtx,
  logout as storeLogout,
  refreshCurrentUser,
  reloadCurrentUser,
  updateProfile as storeUpdateProfile,
  changePassword as storeChangePassword,
  getZaloAuthUrl as storeGetZaloAuthUrl,
  zaloCallback as storeZaloCallback,
  zaloRegister as storeZaloRegister,
  getZaloLinkUrl as storeGetZaloLinkUrl,
  unlinkZalo as storeUnlinkZalo,
  type AuthStatus,
} from '../store/authStore';
import { updateKycStatus as updateLocalKycStatus } from '../store';
import type { UpdateMePayload, ZaloRegisterPayload } from '../api/auth';

export function useAuth() {
  const [state, setState] = useState(getAuthState());

  useEffect(() => subscribeAuth(() => setState(getAuthState())), []);

  const login = useCallback((phone: string, password: string, mfaCode?: string): Promise<User> => {
    return storeLogin(phone, password, mfaCode);
  }, []);

  const registerFarmer = useCallback((data: { phone: string; password: string; fullName: string; htxId?: string }): Promise<void> => {
    return storeRegisterFarmer(data);
  }, []);

  const registerHtx = useCallback((data: { name: string; code: string; address?: string; phone: string; password: string; fullName: string }): Promise<void> => {
    return storeRegisterHtx(data);
  }, []);

  const logout = useCallback((): Promise<void> => {
    return storeLogout();
  }, []);

  const reloadUser = useCallback((): Promise<User> => reloadCurrentUser(), []);

  /** @deprecated local-only display update pending task 21's real eKYC review API wiring. */
  const updateKycStatus = useCallback((userId: string, newStatus: KycStatus, reason?: string, creditScore?: number, riskLevel?: RiskLevel) => {
    updateLocalKycStatus(userId, newStatus, reason, creditScore, riskLevel);
    refreshCurrentUser({ kycStatus: newStatus, kycReason: reason, creditScore, riskLevel });
  }, []);

  const getZaloAuthUrl = useCallback((): Promise<string> => storeGetZaloAuthUrl(), []);

  const zaloCallback = useCallback((code: string, state: string) => storeZaloCallback(code, state), []);

  const zaloRegister = useCallback((payload: ZaloRegisterPayload): Promise<User> => storeZaloRegister(payload), []);

  const getZaloLinkUrl = useCallback((): Promise<string> => storeGetZaloLinkUrl(), []);

  const unlinkZalo = useCallback((): Promise<void> => storeUnlinkZalo(), []);

  const updateProfile = useCallback((patch: UpdateMePayload): Promise<void> => {
    return storeUpdateProfile(patch);
  }, []);

  const changePassword = useCallback((currentPassword: string, newPassword: string): Promise<void> => {
    return storeChangePassword(currentPassword, newPassword);
  }, []);

  return {
    user: state.user,
    isInitializing: state.status === 'initializing',
    status: state.status as AuthStatus,
    login,
    registerFarmer,
    registerHtx,
    logout,
    reloadUser,
    updateKycStatus,
    updateProfile,
    changePassword,
    getZaloAuthUrl,
    zaloCallback,
    zaloRegister,
    getZaloLinkUrl,
    unlinkZalo,
  };
}
